import type { ApolloError } from '@apollo/client';
import { metrics } from '@seek/metrics-js';

import {
  Actions as UnconfirmedDataActions,
  type UnconfirmedDataAction,
} from 'src/components/Profile/UnconfirmedData/actionCreators';
import { RESUME } from 'src/components/Router';
import { isGraphQLAuthError } from 'src/modules/error-handling/error-handling';
import { log } from 'src/modules/error-logger';
import type { ReduxInjections } from 'src/types';
import * as Schema from 'src/types/graphql';

import { Actions, type ResumeAction } from './reducer';
import type { Resume, FileStatus, Origin } from './types';

export const invalidFile = (status: FileStatus): ResumeAction => ({
  type: Actions.RESUME_INVALID_FILE,
  payload: status,
  meta: {
    sync: {
      state: 'SYNCED',
      parent: RESUME,
      id: Actions.RESUME_UPLOAD,
      action: Actions.RESUME_UPLOAD,
    },
  },
});

export const fileOpenError = (error: Error) => {
  log(error, { name: 'Resume' });
  metrics.count('file open error', []);
  return {
    type: Actions.RESUME_FILE_OPEN_ERROR,
    payload: error,
  };
};

export const uploadResume =
  (file: File, isDefault: boolean) =>
  ({ dispatch, createGuid }: ReduxInjections<ResumeAction>) => {
    dispatch({
      type: Actions.RESUME_UPLOAD_START,
      payload: { file, isDefault, id: createGuid() },
      meta: {
        sync: {
          state: 'SYNCING',
          parent: RESUME,
          id: Actions.RESUME_UPLOAD,
          action: Actions.RESUME_UPLOAD,
        },
      },
    });
  };

export const uploadResumeSuccess =
  ({ newResumeId, isDefault }: { newResumeId: string; isDefault: boolean }) =>
  ({
    dispatch,
    appConfig,
  }: ReduxInjections<ResumeAction> &
    ReduxInjections<UnconfirmedDataAction>) => {
    const { showFeature } = appConfig;
    const showResumeParsing = showFeature('resume-parsing');
    if (showResumeParsing) {
      dispatch({
        type: UnconfirmedDataActions.FETCH_UNCONFIRMED_DATA_START,
        payload: {
          contextId: newResumeId,
        },
      });
    }
    dispatch({
      type: Actions.RESUME_UPLOAD_SUCCESS,
      payload: { newResumeId, isDefault, showResumeParsing },
      meta: {
        sync: {
          state: 'SYNCED',
          parent: RESUME,
          id: Actions.RESUME_UPLOAD,
          action: Actions.RESUME_UPLOAD,
        },
        profileUpdate: true,
        metrics: {
          'profile-update': true,
        },
      },
    });
  };

export const uploadResumeFailure = (error: ApolloError): ResumeAction => ({
  type: Actions.RESUME_UPLOAD_FAILURE,
  payload: { error },
  meta: {
    sync: {
      state: 'ERROR',
      parent: RESUME,
      id: Actions.RESUME_UPLOAD,
      action: Actions.RESUME_UPLOAD,
    },
    metrics: {
      error: !isGraphQLAuthError(error),
    },
  },
});

export const resumeUploadMakeDefault = (isChecked: boolean): ResumeAction => ({
  type: Actions.RESUME_UPLOADED_MAKE_DEFAULT_CLICKED,
  payload: {
    isChecked,
  },
});

export const requestDeleteResume = (id: string): ResumeAction => ({
  type: Actions.RESUME_REQUEST_DELETE,
  payload: id,
});

export const cancelDeleteResume = (): ResumeAction => ({
  type: Actions.RESUME_CANCEL_DELETE,
});

const resumeGetSyncProps = (status?: string) =>
  ({
    syncingLabel: 'Downloading',
    doneLabel: status === Schema.VirusScanStatus.Ok ? 'Downloaded' : undefined,
  } as const);

export const getResumeLink = (id: string, origin: Origin): ResumeAction => ({
  type: Actions.RESUME_GET_LINK_START,
  payload: id,
  meta: {
    origin,
    sync: {
      state: 'SYNCING',
      parent: RESUME,
      id,
      action: Actions.RESUME_GET_LINK,
      props: resumeGetSyncProps(),
    },
  },
});

export const getResumeLinkSuccess =
  (input: { link: string; id: string; resume: Resume }) =>
  ({ dispatch, api: { queryGql } }: ReduxInjections<ResumeAction>): void => {
    const { link, id, resume } = input;
    const { virusScanStatus } = resume.fileMetadata;

    queryGql({
      query: Schema.GetResumeByIdDocument,
      variables: { id },
    });

    if (
      virusScanStatus === Schema.VirusScanStatus.Ok &&
      typeof window !== 'undefined'
    ) {
      window.location.href = link;
    }

    dispatch({
      type: Actions.RESUME_GET_LINK_SUCCESS,
      payload: resume,
      meta: {
        sync: {
          state: 'SYNCED',
          parent: RESUME,
          id,
          action: Actions.RESUME_GET_LINK,
          props: resumeGetSyncProps(virusScanStatus),
        },
      },
    });
  };

export const getResumeLinkFailure = (
  error: ApolloError,
  id: string,
): ResumeAction => ({
  type: Actions.RESUME_GET_LINK_FAILURE,
  payload: { error, id },
  meta: {
    sync: {
      state: 'ERROR',
      parent: RESUME,
      id,
      action: Actions.RESUME_GET_LINK,
      props: resumeGetSyncProps(),
    },
    metrics: {
      error: !isGraphQLAuthError(error),
    },
  },
});

export const updateResume = (id: string): ResumeAction => ({
  type: Actions.RESUME_UPDATE_START,
  payload: id,
  meta: {
    sync: {
      state: 'SYNCING',
      parent: RESUME,
      id,
      action: Actions.RESUME_GET_LINK,
    },
  },
});

export const updateResumeSuccess = (resume: Resume): ResumeAction => ({
  type: Actions.RESUME_UPDATE_SUCCESS,
  payload: resume,
  meta: {
    sync: {
      state: 'SYNCED',
      parent: RESUME,
      id: resume.id,
      action: Actions.RESUME_GET_LINK,
    },
  },
});

export const updateResumeFailure = (
  error: ApolloError,
  id: string,
): ResumeAction => ({
  type: Actions.RESUME_UPDATE_FAILURE,
  payload: { error, id },
  meta: {
    sync: {
      state: 'ERROR',
      parent: RESUME,
      id,
      action: Actions.RESUME_GET_LINK,
    },
    metrics: {
      error: !isGraphQLAuthError(error),
    },
  },
});

export const deleteResumeSuccess = (): ResumeAction => ({
  type: Actions.RESUME_DELETE_SUCCESS,
  meta: {
    metrics: {
      'profile-update': true,
    },
  },
});

export const deleteResumeFailure = (error: ApolloError): ResumeAction => ({
  type: Actions.RESUME_DELETE_FAILURE,
  payload: { error },
  meta: {
    metrics: {
      error: !isGraphQLAuthError(error),
    },
  },
});

export const deleteResume = (id: string, origin?: Origin): ResumeAction => ({
  type: Actions.RESUME_DELETE_START,
  payload: { id },
  meta: {
    origin,
  },
});

export const updateDefaultSuccess = () => ({
  type: Actions.RESUME_UPDATE_DEFAULT_SUCCESS,
  meta: {
    metrics: {
      'profile-update': true,
    },
  },
});

export const updateDefaultFailure = (error: ApolloError): ResumeAction => ({
  type: Actions.RESUME_UPDATE_DEFAULT_FAILURE,
  payload: { error },
  meta: {
    metrics: {
      error: !isGraphQLAuthError(error),
    },
  },
});

export const updateDefault = (origin?: Origin): ResumeAction => ({
  type: Actions.RESUME_UPDATE_DEFAULT_START,
  meta: {
    origin,
  },
});

export const resumeEditViewLoaded = (): ResumeAction => ({
  type: Actions.RESUME_EDIT_LOADED,
});
